import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BidRequest } from '../requests/bidRequest';
import { AuthenticationService } from './AuthenticationService';
import { AppConfig } from '../app.config';
import { AcceptRequest } from '../requests/acceptRequest';
import { BasicResponse } from '../responses/basicResponse';
import { PrivateAuctionAuthorizationRequest } from '../requests/privateAuctionAuthorizationRequest';
import { UpdatePrivateAuctionAuthorizationStatusRequest } from '../requests/updatePrivateAuctionAuthorizationStatusRequest';
import { EqualizeUserBidForAuctionRequest } from '../requests/equalizeUserBidForAuctionRequest';
import { LoggingService } from './LoggingService';

@Injectable({
  providedIn: 'root'
})
export class AuctionManagementApiService {
  
 
  constructor(private httpClient: HttpClient, private appConfig:AppConfig, private authService:AuthenticationService, private logger:LoggingService) { 
  }
  get businessId():string{
    return this.appConfig.businessId;
  }
  get serviceURL():string {
    return this.appConfig.getConfig("auctionmanagementapi_external_url") + '/';
  }
  get optionsJson(){
    return this.appConfig.optionsJson;
  }
  get optionsForm(){
    return this.appConfig.optionsForm;
  }


  public placeBid(bid:BidRequest):Observable<any>{
    bid.userId=this.authService.currentUserValue!.id;
    this.logger.log("placeBid:", bid);
    return this.httpClient.post<any>(this.serviceURL+`bid`, bid, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public makeOffer(bid:BidRequest):Observable<any>{
    bid.userId=this.authService.currentUserValue!.id;
    this.logger.log("makeOffer:", bid);
    return this.httpClient.post<any>(this.serviceURL+`offer`, bid, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public acceptOffer(accept:AcceptRequest):Observable<any>{
    accept.userId=this.authService.currentUserValue!.id;
    this.logger.log("acceptOffer:", accept);
    return this.httpClient.post<any>(this.serviceURL+`acceptOffer`, accept, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public buyNow(bid:BidRequest):Observable<any>{
    bid.userId=this.authService.currentUserValue!.id;
      this.logger.log("buyNow:", bid);
    return this.httpClient.post<any>(this.serviceURL+`buyNow`, bid, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionEnding(auctionId:number, scheduleSeqNo:number){
    this.logger.log("Getting auction ending for: " + auctionId);
    return this.httpClient.get<any>(this.serviceURL+`auctionEnding/` + auctionId.toString() + (scheduleSeqNo?('/' + scheduleSeqNo):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionBidHistory(auctionId:number, scheduleSeqNo:number){
    this.logger.log("Getting auction bid history for: " + auctionId);
    return this.httpClient.get<any>(this.serviceURL+`auctionBidHistory/` + auctionId.toString() + (scheduleSeqNo?('/' + scheduleSeqNo):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  /** the logged in user's pseudonymous bidder key for an auction (bids show bidder keys instead of user ids) */
  public getBidderKey(auctionId:number){
    return this.httpClient.get<any>(this.serviceURL+`bidderKey/` + auctionId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionBidRanking(auctionId:number, scheduleSeqNo:number){
    this.logger.log("Getting auction bid ranking for: " + auctionId);
    return this.httpClient.get<any>(this.serviceURL+`auctionBidRanking/` + auctionId.toString() + (scheduleSeqNo?('/' + scheduleSeqNo):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyBidHistory(propertyId:number){
    this.logger.log("Getting property bid history for: " + propertyId);
    return this.httpClient.get<any>(this.serviceURL+`propertyBidHistory/`+ propertyId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  /** bid history of a property with the masked bids of the public view, one entry per auction sequence (client users) */
  public getPropertyBidHistoryAnonymized(propertyId:number){
    return this.httpClient.get<any>(this.serviceURL+`propertyBidHistoryAnonymized/`+ propertyId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public requestPrivateAuctionAuthorization(paa:PrivateAuctionAuthorizationRequest){
    paa.userId=this.authService.currentUserValue!.id;
    this.logger.log("paa: ",paa);
    return this.httpClient.post<any>(this.serviceURL+`requestPrivateAuctionAuthorization`, paa, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public verifyPrivateAuctionAuthorization(paa:PrivateAuctionAuthorizationRequest){
    paa.userId=this.authService.currentUserValue!.id;
    this.logger.log("paa: ",paa);
    return this.httpClient.post<any>(this.serviceURL+`verifyPrivateAuctionAuthorization`, paa, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updatePrivateAuctionAuthorizationStatus(upaas:UpdatePrivateAuctionAuthorizationStatusRequest){
    upaas.userId=this.authService.currentUserValue!.id;
    upaas.changedBy=this.authService.currentUserValue!.fullName;
    this.logger.log("upaas: ", upaas);
    return this.httpClient.post<any>(this.serviceURL+`updatePrivateAuctionAuthorizationStatus`, upaas, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPrivateAuctionAuthorizations(auctionId:number, userId:string | undefined){
    this.logger.log("Getting private auction authorizations for: " + auctionId);
    return this.httpClient.get<any>(this.serviceURL+`privateAuctionAuthorizations/`+ auctionId.toString() + (userId?('/' + userId):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public equalizeUserBidForAuction(eub:EqualizeUserBidForAuctionRequest){
    eub.userId=this.authService.currentUserValue!.id;
    this.logger.log("eub: ", eub);
    return this.httpClient.post<any>(this.serviceURL+`equalizeUserBidForAuction`, eub, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  private createBasicResponseError(error: HttpErrorResponse){
    let br = new BasicResponse();
    br.success=false;
    br.message='Backend error - please try again later';
    return br;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
        // A client-side or network error occurred. Handle it accordingly.
        console.error('An error occurred:', error.error.message);
    } else {
        // The backend returned an unsuccessful response code.
        // The response body may contain clues as to what went wrong,
        console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(()=>(error));
  }
}
